import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  CssBaseline,
  Grid,
  Button,
  MobileStepper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ThemeProvider, styled } from '@mui/material/styles';
import ReactPlayer from 'react-player';

import * as constructionWorkTypeActions from './store/constructionWorkType';
import * as commonActions from './store/common';
import theme from './theme';

const StyledButton = styled((props) => (
  <Button {...props}/>
))(
    ({ theme, ...props }) => {
      const { sx } = { ...props };
      return {
        '&:hover': {
          backgroundColor: sx.bgcolor,
          filter: 'brightness(120%)',
        },
    }}
);

const Intro = () => {  
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const constructionWorkTypes = useSelector(state => state.constructionWorkType.constructionWorkTypes);
  const manageStatus = useSelector(state => state.common.manageStatus);
  const [activeStep, setActiveStep] = useState(0);
  
  // 스와이프 관련 ref (리렌더링 방지)
  const touchRef = useRef({ startX: 0, startY: 0, endX: 0, isSwiping: false, targetUrl: null });

  const dispatch = useDispatch();

  const selectConstructionTypes = () => dispatch(constructionWorkTypeActions.selectAll())
  const selectManageStatus = () => dispatch(commonActions.selectManageStatus())

  useEffect(
    async () => {
      await selectConstructionTypes();
      await selectManageStatus();
    }, []
  )

  // 공종을 4개씩 그룹화
  const itemsPerPage = 4;
  const chunkedTypes = constructionWorkTypes ? 
    Array.from({ length: Math.ceil(constructionWorkTypes.length / itemsPerPage) }, (_, i) =>
      constructionWorkTypes.slice(i * itemsPerPage, i * itemsPerPage + itemsPerPage)
    ) : [];
  const maxSteps = chunkedTypes.length;

  // 터치 스와이프 핸들러 (오버레이용)
  const minSwipeDistance = 30; // X축 최소 스와이프 거리
  const handleOverlayTouchStart = (e, url) => {
    touchRef.current.startX = e.touches[0].clientX;
    touchRef.current.startY = e.touches[0].clientY;
    touchRef.current.endX = e.touches[0].clientX;
    touchRef.current.endY = e.touches[0].clientY;
    touchRef.current.isSwiping = false;
    touchRef.current.targetUrl = url;
  };
  const handleOverlayTouchMove = (e) => {
    touchRef.current.endX = e.touches[0].clientX;
    touchRef.current.endY = e.touches[0].clientY;
    const distanceX = Math.abs(touchRef.current.startX - touchRef.current.endX);
    const distanceY = Math.abs(touchRef.current.startY - touchRef.current.endY);
    // X축 이동이 Y축보다 크고, 최소 거리 이상일 때만 스와이프로 판단
    if (distanceX > minSwipeDistance && distanceX > distanceY * 1.5) {
      touchRef.current.isSwiping = true;
    }
  };
  const handleOverlayTouchEnd = (e) => {
    const distanceX = touchRef.current.startX - touchRef.current.endX;
    const distanceY = Math.abs(touchRef.current.startY - touchRef.current.endY);
    const absDistanceX = Math.abs(distanceX);
    
    // X축 이동이 Y축의 1.5배 이상이고, 최소 거리 이상일 때만 페이지 전환
    const isHorizontalSwipe = absDistanceX > minSwipeDistance && absDistanceX > distanceY * 1.5;
    const isLeftSwipe = distanceX > 0 && isHorizontalSwipe;
    const isRightSwipe = distanceX < 0 && isHorizontalSwipe;
    
    if (isLeftSwipe && activeStep < maxSteps - 1) {
      setActiveStep(prev => prev + 1);
    } else if (isRightSwipe && activeStep > 0) {
      setActiveStep(prev => prev - 1);
    } else if (!touchRef.current.isSwiping && touchRef.current.targetUrl) {
      // 스와이프가 아니면 클릭으로 처리
      const url = touchRef.current.targetUrl;
      if (url.startsWith('http://') || url.startsWith('https://')) {
        window.open(url, '_blank');
      } else {
        navigate(url);
      }
    }
    touchRef.current.isSwiping = false;
  };

  // TODO : 추후 많이 사용하면 utils로 뺄 것
  const isAbsoluteUrl = (url) => {
    return url && (url.startsWith('http://') || url.startsWith('https://'));
  };

  const Types = ({ name, comments, color, url, col, xsCol, isLast, isMobileSlider, onTouchStart, onTouchMove, onTouchEnd }) => {
    return (
      <Grid item xs={xsCol || 12} sm={col} display="flex" justifyContent={"center"} alignItems="center">
        <Box
          sx={{
            width: '100%',
            height: { xs: '100px', mobile: '140px' },
            transition: 'transform 0.3s ease',
            '&:hover': {
              transform: { xs: 'scale(1.05)', mobile: 'scale(1.6)' },
              zIndex: 1
            },
            position: 'relative',
          }}
        >
          {/* 모바일 슬라이더용 투명 오버레이 */}
          {isMobileSlider && (
            <Box
              onTouchStart={(e) => onTouchStart(e, url)}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 10,
                touchAction: 'pan-y',
              }}
            />
          )}
          <StyledButton 
            component={isAbsoluteUrl(url) ? 'a' : Link} 
            to={!isAbsoluteUrl(url) ? (url || "") : undefined}
            href={isAbsoluteUrl(url) ? url : undefined}
            target={isAbsoluteUrl(url) ? "_blank" : undefined}
            variant="contained" 
            sx={{
              width: '100%',
              height: '100%',
              // 모바일(xs, mobile)에서는 모든 공종 동일 크기, sm 이상에서만 isLast 크기 차등
              fontSize: { xs: '0.9rem', mobile: '0.9rem', sm: isLast ? '2rem' : '1.06rem' },
              fontWeight: 'bold',
              bgcolor: color || '#aaa',
              borderRadius: 0,
              position: 'relative',
              overflow: 'hidden',  // 중요: 그라데이션을 버튼 내부로 제한
              // ...(isLast && {
              //   '&::before': {
              //     content: '""',
              //     position: 'absolute',
              //     top: 0,
              //     left: 0,
              //     right: 0,
              //     bottom: 0,
              //     background: `linear-gradient(90deg, 
              //       transparent 0%, 
              //       rgba(255,255,255,0.6) 50%, 
              //       transparent 100%)`,
              //     animation: 'shimmerAndFlash 3s infinite',
              //     zIndex: 1
              //   },
              //   '@keyframes shimmerAndFlash': {
              //     '0%': {
              //       transform: 'translateX(-100%)',
              //     },
              //     '85%': {
              //       transform: 'translateX(100%)',
              //       opacity: 0.6
              //     },
              //     '90%': {
              //       transform: 'translateX(100%)',
              //       opacity: 1
              //     },
              //     '100%': {
              //       transform: 'translateX(100%)',
              //       opacity: 0.6
              //     }
              //   }
              // })
              ...(isLast && {
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(90deg, 
                    transparent 0%, 
                    rgba(255,255,255,0.6) 50%, 
                    transparent 100%)`,
                  animation: 'shimmerAndFlash 3s infinite',
                  zIndex: 1
                },
                '@keyframes shimmerAndFlash': {
                  '0%': {
                    transform: 'translateX(-100%)',
                  },
                  '100%': {
                    transform: 'translateX(100%)',
                  }
                }
              })
            }}
          >
            <Grid
              container
              spacing={0}
              sx={{
                position: 'relative',
                zIndex: 2,
                ...(isLast && {
                  animation: 'textJump 3s infinite',
                  '@keyframes textJump': {
                    '0%': {
                      transform: 'translateY(0)',
                    },
                    '45%': {
                      transform: 'translateY(0)',
                    },
                    '48%': {
                      transform: 'translateY(-25px)',
                    },
                    '52%': {
                      transform: 'translateY(0)',
                      transition: 'transform 0.2s cubic-bezier(.17,.67,.83,.67)'  // 빠르게 떨어지는 효과
                    }
                  }
                })
              }}
            >
              <Grid item xs={12} display="flex" justifyContent={"center"} alignItems="center">
                {name}
              </Grid>
              <Grid item xs={12} display="flex" justifyContent={"center"} sx={{ fontSize: { xs: '0.65em', mobile: '0.7em' } }}>
                {comments || '준비중 입니다...'}
              </Grid>
              {name === '유리공사' && manageStatus && (
                <Grid item xs={12} sx={{ 
                  mt: { xs: 0.5, sm: 1 },
                  fontSize: { xs: '0.6em', sm: '0.7em' },
                  height: { xs: '18px', mobile: '20px' },
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  <Box sx={{
                    // '@keyframes slideUp': {
                    //   '0%': { transform: 'translateY(0)' },
                    //   '23%': { transform: 'translateY(0)' },
                    //   '25%': { transform: 'translateY(-20px)' },
                    //   '48%': { transform: 'translateY(-20px)' },
                    //   '50%': { transform: 'translateY(-40px)' },
                    //   '73%': { transform: 'translateY(-40px)' },
                    //   '75%': { transform: 'translateY(-60px)' },
                    //   '98%': { transform: 'translateY(-60px)' },
                    //   '100%': { transform: 'translateY(-80px)' }
                    // },
                    '@keyframes slideUp': {
                      '0%': { transform: 'translateY(0)' },
                      '48%': { transform: 'translateY(0)' },
                      '50%': { transform: 'translateY(-20px)' },
                      '98%': { transform: 'translateY(-20px)' },
                      '100%': { transform: 'translateY(-40px)' }
                    },
                    animation: 'slideUp 12s infinite linear',
                    position: 'absolute',
                    width: '100%'
                  }}>
                    <Box sx={{ height: { xs: '18px', mobile: '20px' }, display: 'flex', justifyContent: 'center' }}>
                      <span>회원사</span>
                      <span style={{ marginLeft: '5px' }}>{manageStatus.gclientCnt.toLocaleString()} 개사</span>
                    </Box>
                    <Box sx={{ height: { xs: '18px', mobile: '20px' }, display: 'flex', justifyContent: 'center' }}>
                      <span>등록문서</span>
                      <span style={{ marginLeft: '5px' }}>{manageStatus.g04docuCnt.toLocaleString()} 건</span>
                    </Box>
                    {/* 현재는 발급수가 적어 노출하지 않음 */}
                    {/* <Box sx={{ height: '20px', display: 'flex', justifyContent: 'center' }}>
                      <span>자재승인서</span>
                      <span style={{ marginLeft: '5px' }}>{manageStatus.materialApprovalCnt.toLocaleString()} 건</span>
                    </Box>
                    <Box sx={{ height: { xs: '18px', mobile: '20px' }, display: 'flex', justifyContent: 'center' }}>
                      <span>납품확인서</span>
                      <span style={{ marginLeft: '5px' }}>{manageStatus.supplyConfirmCnt.toLocaleString()} 건</span>
                    </Box> */}
                    {/* 첫 번째 항목을 다시 한번 추가하여 끊김 없는 롤링 구현 */}
                    <Box sx={{ height: { xs: '18px', mobile: '20px' }, display: 'flex', justifyContent: 'center' }}>
                      <span>회원사</span>
                      <span style={{ marginLeft: '5px' }}>{manageStatus.gclientCnt.toLocaleString()} 개사</span>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </StyledButton>
        </Box>
      </Grid>
    )
  }

  return (
    <ThemeProvider theme={theme}>
      <Container
        component="main"
        maxWidth="false"
        sx={{
          mx: 'auto',
          bgcolor: '#000000',
          minHeight: { xs: 0, mobile: 'calc(100vh - 275px)' },
          py: { xs: 2, mobile: 0 },
          px: { xs: 2, mobile: 2 },
          display: 'flex',
          alignItems: { xs: 'flex-start', mobile: 'center' },
          boxSizing: 'border-box',
        }}
      >
        <CssBaseline />
        {/* 모바일: maxWidth 1536. 데스크탑: 90%로 가로폭 축소 */}
        <Box sx={{ width: '100%', maxWidth: { xs: 1536, sm: '90%' }, mx: { xs: 'auto', sm: 'auto' } }}>
          <Grid container spacing={{ xs: 2, mobile: 3 }} sx={{ mt: { xs: 1, mobile: 5 }, mb: { xs: 1, mobile: -3 }, width: '100%' }}>
            <Grid item xs={12} sm={6} display="flex" justifyContent="center" alignItems="center" sx={{ order: { xs: 1, mobile: 0 } }}>
              <Box sx={{ width: { xs: '100%', sm: '70%' }, maxWidth: '100%', aspectRatio: '16/9', position: 'relative', overflow: 'hidden', borderRadius: 1 }}>
                <ReactPlayer
                  width="100%"
                  height="100%"
                  style={{ position: 'absolute', top: 0, left: 0 }}
                  url={`/intro.mp4`}
                  playing={true}
                  muted={true}
                  controls={true}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ 
              order: { xs: 2, sm: 0 }, 
              width: '100%', 
              boxSizing: 'border-box', 
              display: 'flex', 
              justifyContent: 'center',
              flexDirection: 'column',
              alignItems: 'center',
            }}>
              {/* 모바일: 슬라이더 형태 */}
              {isMobile && constructionWorkTypes && constructionWorkTypes.length > 0 && (
                <Box sx={{ width: '100%' }}>
                  {/* 도트 인디케이터 - 공종 버튼 상단 */}
                  {maxSteps > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 1, py: 1 }}>
                      {chunkedTypes.map((_, index) => (
                        <Box
                          key={index}
                          onClick={() => setActiveStep(index)}
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: index === activeStep ? 'white' : 'rgba(255,255,255,0.3)',
                            cursor: 'pointer',
                            transition: 'background-color 0.3s',
                          }}
                        />
                      ))}
                    </Box>
                  )}
                  {/* 슬라이더 컨테이너 - overflow hidden */}
                  <Box sx={{ overflow: 'hidden', py: 1 }}>
                    {/* 슬라이드 트랙 - 모든 페이지를 가로로 나열 */}
                    <Box
                      sx={{
                        display: 'flex',
                        transition: 'transform 0.3s ease-out',
                        transform: `translateX(-${activeStep * 100}%)`,
                      }}
                    >
                      {chunkedTypes.map((pageTypes, pageIdx) => (
                        <Box
                          key={pageIdx}
                          sx={{
                            width: '100%',
                            minWidth: '100%',
                            flexShrink: 0,
                            px: 1,
                            boxSizing: 'border-box',
                          }}
                        >
                          <Grid container spacing={1}>
                            {pageTypes.map((type, idx) => (
                              <Types
                                key={type.id || type.index}
                                name={type.name}
                                comments={type.comments}
                                color={type.color}
                                url={type.url}
                                xsCol={12}
                                col={12}
                                isLast={false}
                                isMobileSlider={true}
                                onTouchStart={handleOverlayTouchStart}
                                onTouchMove={handleOverlayTouchMove}
                                onTouchEnd={handleOverlayTouchEnd}
                              />
                            ))}
                          </Grid>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              )}
              {/* 데스크탑: 기존 그리드 형태 */}
              {!isMobile && (
                <Grid container spacing={1.5} sx={{ 
                  p: { xs: 2, mobile: 5 }, 
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box'
                }}>
                {
                  constructionWorkTypes &&
                  constructionWorkTypes.length > 0 && (
                    constructionWorkTypes.map((type, idx) => <Types
                      key={type.id || type.index}
                      name={type.name}
                      comments={type.comments}
                      color={type.color}
                      url={type.url}
                      // 프로모션 팝업 유리공사의 로그인페이지에 노출하므로 아래는 기존처럼 공종 블럭 나오도록 함
                      col={idx === constructionWorkTypes.length - 1 && constructionWorkTypes.length > 100 ? 12 : 3}
                      isLast={idx === constructionWorkTypes.length - 1 && constructionWorkTypes.length > 100}
                    />)
                  )
                }
                </Grid>
              )}
            </Grid>
          </Grid>
        </Box>
      </Container>
    </ThemeProvider>
  );
}

export default Intro;